import { CustomToolParameterType } from "./custom.js";
const customFieldValueType = "string | number | boolean | undefined";
function buildStringProperty(name, doc) {
  return { name, type: "string | undefined", kind: "property", doc };
}
function buildNullableStringProperty(name, doc) {
  return { name, type: "string | null", kind: "property", doc };
}
function buildCustomFieldsMember(doc, fields) {
  return {
    name: "custom_fields",
    kind: "property",
    doc,
    members: fields.map((field) => ({
      name: field.field_name,
      type: customFieldValueType,
      kind: "property",
      label: field.display_name,
      doc: ""
    }))
  };
}
function buildParameterType(parameter) {
  switch (parameter.type) {
    case CustomToolParameterType.ARRAY: {
      const itemType = parameter.array_item_type === CustomToolParameterType.OBJECT ? "Record<string, unknown>" : parameter.array_item_type ?? CustomToolParameterType.STRING;
      return `${itemType}[] | undefined`;
    }
    case CustomToolParameterType.OBJECT:
      return "Record<string, unknown> | undefined";
    default:
      return `${parameter.type} | undefined`;
  }
}
const logFunctionType = "(...values: unknown[]) => void";
const codeToolScriptGlobals = [
  {
    name: "fetch",
    type: '(url: string | URL, init?: { method?: "GET" | "POST"; headers?: Record<string, string>; body?: string }) => Promise<{ status: number; ok: boolean; headers: { get(name: string): string | null }; text(): Promise<string>; json(): Promise<unknown> }>',
    kind: "function",
    doc: "Sends a GET or POST, with a string body, to one of the tool's allowed domains. At most a few per run; the response is read in full."
  },
  {
    name: "URL",
    type: "typeof URL",
    kind: "class",
    doc: "Parses and builds URLs, with `searchParams`."
  },
  {
    name: "URLSearchParams",
    type: "typeof URLSearchParams",
    kind: "class",
    doc: "Reads and builds query strings."
  },
  {
    name: "console",
    kind: "property",
    doc: "Writes to the run's logs.",
    members: ["log", "info", "warn", "error"].map((level) => ({
      name: level,
      type: logFunctionType,
      kind: "function",
      doc: `Logs at the ${level} level.`
    }))
  },
  {
    name: "time",
    kind: "property",
    doc: "Dates and times, as ISO strings; the sandbox has no `Intl`.",
    members: [
      {
        name: "now",
        type: "() => string",
        kind: "function",
        doc: "The run's current time, fixed for the whole run."
      },
      {
        name: "parse",
        type: "(text: string, options?: { format?: string; zone?: string }) => string",
        kind: "function",
        doc: "Parses ISO text, or text in a Luxon `format`, in `zone` (UTC by default)."
      },
      {
        name: "format",
        type: "(value: string, options?: { zone?: string; locale?: string; format?: string; preset?: string }) => string",
        kind: "function",
        doc: "Formats in `zone` with a Luxon `format`, or a `preset`: datetime_full (default), datetime_med, datetime_short, date_full, date_med, date_short, time_simple."
      },
      {
        name: "plus",
        type: "(value: string, duration: Record<string, number>) => string",
        kind: "function",
        doc: "Adds a duration such as `{ days: 1 }`."
      },
      {
        name: "minus",
        type: "(value: string, duration: Record<string, number>) => string",
        kind: "function",
        doc: "Subtracts a duration such as `{ hours: 2 }`."
      },
      {
        name: "diff",
        type: "(from: string, to: string, unit: string) => number",
        kind: "function",
        doc: "`from` minus `to`, in years, months, weeks, days, hours, minutes, seconds or milliseconds."
      }
    ]
  }
];
function buildCodeToolScriptApi(options) {
  const input = {
    name: "input",
    kind: "property",
    doc: "The call this tool runs on, and the parameters the agent supplied.",
    members: [
      {
        name: "params",
        kind: "property",
        doc: "The tool's parameters as the agent supplied them; absent when left out.",
        members: options.parameters.map((parameter) => ({
          name: parameter.name,
          type: buildParameterType(parameter),
          kind: "property",
          doc: parameter.description ?? ""
        }))
      },
      {
        name: "contact",
        kind: "property",
        doc: "The caller as the contacts API returns them, a field `null` when unset.",
        members: [
          {
            name: "id",
            type: "string",
            kind: "property",
            doc: "The contact's id; a placeholder in a simulation."
          },
          buildNullableStringProperty("email", "Email address."),
          buildNullableStringProperty("phone_number", "Phone number."),
          buildNullableStringProperty("ext_id", "Your own id for the contact."),
          buildNullableStringProperty("first_name", "First name."),
          buildNullableStringProperty("last_name", "Last name."),
          buildCustomFieldsMember(
            "The contact's custom field values, by key.",
            options.contactCustomFields
          )
        ]
      },
      {
        name: "customer_interaction",
        kind: "property",
        doc: "The customer interaction this call is recorded as.",
        members: [
          {
            name: "id",
            type: "string",
            kind: "property",
            doc: "The customer interaction's id; a placeholder in a simulation."
          },
          buildCustomFieldsMember(
            "The interaction's custom field values, by key.",
            options.interactionCustomFields
          )
        ]
      },
      {
        name: "call",
        kind: "property",
        doc: "The phone call; absent on a call-task turn, which has no call in progress.",
        members: [
          {
            name: "direction",
            type: '"inbound" | "outbound"',
            kind: "property",
            doc: "Whether the call came in or went out."
          },
          buildNullableStringProperty(
            "user_phone_number",
            "The other party's number, whatever the direction; null when withheld or unknown."
          ),
          buildStringProperty(
            "ext_id",
            "Your own id for the call, when you gave one."
          )
        ]
      },
      {
        name: "call_task",
        kind: "property",
        doc: "The call task this run belongs to: on a task's turn, and on a call that belongs to a task.",
        members: [
          {
            name: "id",
            type: "string",
            kind: "property",
            doc: "The call task's id; a placeholder in a simulation."
          },
          buildStringProperty(
            "ext_id",
            "Your own id for the task, when it has one."
          )
        ]
      },
      {
        name: "company_timezone",
        type: "string",
        kind: "property",
        doc: "The company's default time zone, such as `America/New_York`."
      }
    ]
  };
  const scriptContext = {
    name: "context",
    kind: "property",
    doc: "What the tool brings to the run.",
    members: [
      {
        name: "secrets",
        kind: "property",
        doc: "The tool's secrets, by name. Each is sent only to its own domains.",
        members: options.secretNames.map((name) => ({
          name,
          type: "string",
          kind: "property",
          doc: "A secret: usable in a request to its domains, and redacted from logs and results."
        }))
      },
      {
        name: "endCall",
        type: "() => ToolOutput",
        kind: "function",
        doc: "Return its result to end the call once the agent has finished saying what it was saying: `return context.endCall()`. Nothing is said after this tool, so it suits a path where no reply is needed. Calling it without returning its result has no effect. It throws when there is no call to end, such as on a call task's turn; catch it to carry on."
      }
    ]
  };
  return {
    parameters: [input, scriptContext],
    globals: codeToolScriptGlobals
  };
}
export {
  buildCodeToolScriptApi
};
//# sourceMappingURL=codeToolScriptApi.js.map
